import React, { useState, useCallback, useRef } from "react";
import {
  X,
  Usb,
  Search,
  RefreshCw,
  Check,
  AlertCircle,
  Download,
  Upload,
  CheckCircle,
  AlertTriangle,
  Repeat,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import Modal from "components/common/Modal";
import { useSerialPort } from "hooks/useSerialPort";
import { useSerialCommand } from "hooks/useSerialCommand";
import { parseImpactDump } from "utils/serialParsers";
import { getWearableByMac } from "constants/wearables";
import betaAPI from "v2/api";
import SerialTerminal from "../common/SerialTerminal";

const STEPS = {
  CONNECT: "connect",
  SELECT_PORT: "select_port",
  DUMPING: "dumping",
  PREVIEW: "preview",
  UPLOADING: "uploading",
  SUCCESS: "success",
};

const normalizeMac = (mac) => (mac || "").toUpperCase().replace(/[:-]/g, "");

const getAssignmentLabel = (a, t) =>
  a.player_name ||
  a.player?.name ||
  t("session.upload.playerFallback", { id: a.player_id || a.id });

const PlayerPicker = ({ assignments = [], selectedId, onSelect }) => {
  const { t } = useTranslation();
  if (!assignments.length) {
    return (
      <p className="py-2 text-center text-[12px] text-[#9CA3AF]">
        {t("session.upload.noPlayers")}
      </p>
    );
  }

  return (
    <div className="max-h-[180px] overflow-y-auto rounded-lg border border-[#F3F4F6]">
      {assignments.map((a) => {
        const id = a.id;
        const name = getAssignmentLabel(a, t);
        const wearableInfo = a.wearable_mac
          ? getWearableByMac(a.wearable_mac)
          : null;
        const isSelected = id === selectedId;

        return (
          <button
            key={id}
            onClick={() => onSelect(a)}
            className={`flex w-full items-center gap-3 border-b border-[#F3F4F6] px-4 py-2.5 text-left transition duration-150 last:border-b-0 ${
              isSelected ? "bg-[#FEF7F3]" : "bg-white hover:bg-[#FAFAFA]"
            }`}
          >
            <div
              className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md ${
                isSelected ? "bg-[#E07A3A]" : "bg-[#F3F4F6]"
              }`}
            >
              {isSelected ? (
                <Check size={12} className="text-white" />
              ) : (
                <span className="text-[10px] font-medium text-[#6B7280]">
                  {name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-[#111827]">
                {name}
              </p>
              {wearableInfo && (
                <p className="truncate text-[10px] text-[#9CA3AF]">
                  {wearableInfo.label}
                </p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};

const WearableUploadModal = ({
  show,
  onClose,
  assignments,
  workspaceId,
  teamId,
  sessionId,
  onUploadComplete,
}) => {
  const { t } = useTranslation();
  const {
    ports,
    connected,
    receivedData,
    error: serialError,
    listPorts,
    connect,
    disconnect,
  } = useSerialPort();

  const { executeCommand, isExecuting, linesReceived, cancel } =
    useSerialCommand();

  const [step, setStep] = useState(STEPS.CONNECT);
  const [scanning, setScanning] = useState(false);
  const [selectedPort, setSelectedPort] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [dumpData, setDumpData] = useState(null);
  const [matchedAssignment, setMatchedAssignment] = useState(null);
  const [wearableLabel, setWearableLabel] = useState(null);
  const [dumpError, setDumpError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [uploadedPlayerName, setUploadedPlayerName] = useState(null);
  const [overrideAssignment, setOverrideAssignment] = useState(null);
  const [showPlayerPicker, setShowPlayerPicker] = useState(false);
  const scanKeyRef = useRef(0);

  const resetState = useCallback(() => {
    setStep(STEPS.CONNECT);
    setScanning(false);
    setSelectedPort(null);
    setConnecting(false);
    setDumpData(null);
    setMatchedAssignment(null);
    setWearableLabel(null);
    setDumpError(null);
    setUploadError(null);
    setUploadedCount(0);
    setUploadedPlayerName(null);
    setOverrideAssignment(null);
    setShowPlayerPicker(false);
  }, []);

  const handleScan = async () => {
    setScanning(true);
    setSelectedPort(null);
    scanKeyRef.current += 1;
    await listPorts();
    setScanning(false);
    setStep(STEPS.SELECT_PORT);
  };

  const handleConfirm = useCallback(async () => {
    if (!selectedPort) return;

    // Connect
    setConnecting(true);
    const result = await connect(selectedPort);
    setConnecting(false);

    if (!result) {
      // Connection failed — error shown via serialError
      return;
    }

    // Transition to DUMPING
    setStep(STEPS.DUMPING);
    setDumpError(null);

    try {
      const cmdResult = await executeCommand({
        command: "d",
        startMarker: "<<<IMPACT_DUMP_START>>>",
        endMarker: "<<<IMPACT_DUMP_END>>>",
        timeout: 15000,
      });

      const parsed = parseImpactDump(cmdResult.lines);
      setDumpData(parsed);

      if (parsed.error && parsed.files.length === 0) {
        setDumpError(parsed.error);
        setStep(STEPS.PREVIEW);
        return;
      }

      if (!parsed.mac) {
        setDumpError(t("session.upload.errors.noMac"));
        setStep(STEPS.PREVIEW);
        return;
      }

      // Match MAC against assignments
      const normalizedDumpMac = normalizeMac(parsed.mac);
      const matched = (assignments || []).find(
        (a) => normalizeMac(a.wearable_mac) === normalizedDumpMac
      );
      setMatchedAssignment(matched || null);

      // Get wearable label
      const wearable = getWearableByMac(parsed.mac);
      setWearableLabel(wearable ? wearable.label : parsed.mac);

      setStep(STEPS.PREVIEW);
    } catch (err) {
      setDumpError(err.message || t("session.upload.errors.downloadFailed"));
      setStep(STEPS.PREVIEW);
    }
  }, [selectedPort, connect, executeCommand, assignments, t]);

  const effectiveAssignment = overrideAssignment || matchedAssignment;

  const handleUpload = useCallback(async () => {
    if (!dumpData || !effectiveAssignment) return;

    setUploadError(null);
    setStep(STEPS.UPLOADING);

    const impactPayload = dumpData.files.flatMap((file) =>
      file.impacts.map((imp) => ({
        linear_g: imp.linearG,
        rotational: imp.rotationalRadS2,
        timestamp_ms: imp.timestampMs,
        boot: imp.boot,
        impact_id: imp.impactId,
        source: "post-session",
      }))
    );

    try {
      await betaAPI.uploadImpactData({
        workspaceId,
        teamId,
        sessionId,
        assignmentId: effectiveAssignment.id,
        body: { impact_data: impactPayload },
      });

      const name =
        effectiveAssignment.player_name ||
        effectiveAssignment.player?.name ||
        t("session.upload.athleteFallback");
      setUploadedCount(impactPayload.length);
      setUploadedPlayerName(name);
      setStep(STEPS.SUCCESS);
    } catch (err) {
      setUploadError(err.message || t("session.upload.errors.uploadFailed"));
      setStep(STEPS.PREVIEW);
    }
  }, [dumpData, effectiveAssignment, workspaceId, teamId, sessionId, t]);

  const handleDone = useCallback(() => {
    onUploadComplete?.();
    disconnect();
    resetState();
    onClose();
  }, [onUploadComplete, disconnect, resetState, onClose]);

  const handleClose = useCallback(() => {
    if (isExecuting) cancel();
    disconnect();
    resetState();
    onClose();
  }, [isExecuting, cancel, disconnect, resetState, onClose]);

  const playerName = effectiveAssignment
    ? effectiveAssignment.player_name ||
      effectiveAssignment.player?.name ||
      t("session.upload.athleteFallback")
    : null;

  return (
    <Modal show={show} onClose={handleClose} maxWidth="520px">
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-4"
        style={{
          background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
        }}
      >
        <h2 className="text-base font-semibold text-white">
          {t("session.upload.title")}
        </h2>
        <button
          onClick={handleClose}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-white/80 transition duration-150 hover:bg-white/10 hover:text-white"
        >
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div className="px-6 py-5">
        {/* ── CONNECT step ── */}
        {step === STEPS.CONNECT && (
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-5">
              <div
                className="absolute inset-0 left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(232, 133, 74, 0.12) 0%, rgba(232, 133, 74, 0) 70%)",
                }}
              />
              <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-[#FEF3E7]">
                <Usb size={32} className="text-[#E07A3A]" />
              </div>
            </div>

            <h3 className="mb-2 text-[15px] font-semibold text-[#111827]">
              {t("session.upload.connect.title")}
            </h3>
            <p className="mb-6 max-w-[320px] text-sm leading-relaxed text-[#6B7280]">
              {t("session.upload.connect.body")}
            </p>

            <div className="mb-6 w-full space-y-3">
              {[
                t("session.upload.connect.step1"),
                t("session.upload.connect.step2"),
                t("session.upload.connect.step3"),
              ].map((text, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 rounded-lg bg-[#F9FAFB] px-4 py-3 text-left"
                >
                  <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#E07A3A] text-[10px] font-bold text-white">
                    {i + 1}
                  </div>
                  <p className="text-[13px] text-[#374151]">{text}</p>
                </div>
              ))}
            </div>

            <button
              onClick={handleScan}
              disabled={scanning}
              className="flex w-full items-center justify-center gap-2 rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition duration-150 disabled:opacity-60"
              style={{
                background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
              }}
            >
              {scanning ? (
                <>
                  <RefreshCw size={15} className="animate-spin" />
                  {t("session.upload.connect.scanning")}
                </>
              ) : (
                <>
                  <Search size={15} />
                  {t("session.upload.connect.scan")}
                </>
              )}
            </button>
          </div>
        )}

        {/* ── SELECT_PORT step ── */}
        {step === STEPS.SELECT_PORT && (
          <div>
            <h3 className="mb-1 text-[15px] font-semibold text-[#111827]">
              {t("session.upload.selectPort.title")}
            </h3>
            <p className="mb-4 text-sm text-[#6B7280]">
              {ports.length > 0
                ? t("session.upload.selectPort.bodyFound")
                : t("session.upload.selectPort.bodyNone")}
            </p>

            {serialError && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-[#FEF2F2] px-3 py-2.5">
                <AlertCircle
                  size={14}
                  className="flex-shrink-0 text-[#DC2626]"
                />
                <p className="text-xs text-[#DC2626]">{serialError}</p>
              </div>
            )}

            <AnimatePresence mode="wait">
              {ports.length > 0 && !scanning && (
                <motion.div
                  key={`ports-${scanKeyRef.current}`}
                  className="mb-4 space-y-2"
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={{
                    visible: { transition: { staggerChildren: 0.08 } },
                    hidden: {},
                  }}
                >
                  {ports.map((port) => {
                    const isSelected = selectedPort === port.path;
                    return (
                      <motion.button
                        key={port.path}
                        onClick={() => setSelectedPort(port.path)}
                        variants={{
                          hidden: { opacity: 0, y: 12, scale: 0.97 },
                          visible: { opacity: 1, y: 0, scale: 1 },
                        }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition duration-150 ${
                          isSelected
                            ? "border-[#E07A3A] bg-[#FEF7F3]"
                            : "border-[#E5E7EB] bg-white hover:border-[#D1D5DB] hover:bg-[#F9FAFB]"
                        }`}
                      >
                        <div
                          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
                            isSelected ? "bg-[#E07A3A]" : "bg-[#F3F4F6]"
                          }`}
                        >
                          {isSelected ? (
                            <Check size={14} className="text-white" />
                          ) : (
                            <Usb size={14} className="text-[#6B7280]" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[13px] font-medium text-[#111827]">
                            {port.manufacturer || port.path}
                          </p>
                          <p className="truncate text-[11px] text-[#9CA3AF]">
                            {port.path}
                            {port.serialNumber
                              ? ` \u00B7 ${t(
                                  "session.upload.selectPort.serialNumber",
                                  {
                                    serial: port.serialNumber,
                                  }
                                )}`
                              : ""}
                          </p>
                        </div>
                      </motion.button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex items-center gap-3">
              <button
                onClick={handleScan}
                disabled={scanning}
                className="flex items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2.5 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-60"
              >
                <RefreshCw
                  size={13}
                  className={scanning ? "animate-spin" : ""}
                />
                {t("session.upload.selectPort.rescan")}
              </button>
              {ports.length > 0 && (
                <button
                  onClick={handleConfirm}
                  disabled={!selectedPort || connecting}
                  className="flex flex-1 items-center justify-center gap-2 rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition duration-150 disabled:opacity-40"
                  style={{
                    background:
                      "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                  }}
                >
                  {connecting ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      {t("session.upload.selectPort.connecting")}
                    </>
                  ) : (
                    <>
                      <Download size={14} />
                      {t("session.upload.selectPort.connectDownload")}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── DUMPING step ── */}
        {step === STEPS.DUMPING && (
          <div className="flex flex-col items-center py-6 text-center">
            <div className="relative mb-5 flex items-center justify-center">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={`pulse-${i}`}
                  className="absolute rounded-full border border-[#E07A3A]"
                  initial={{ width: 64, height: 64, opacity: 0.5 }}
                  animate={{
                    width: [64, 140],
                    height: [64, 140],
                    opacity: [0.4, 0],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: i * 0.5,
                    ease: "easeOut",
                  }}
                />
              ))}
              <motion.div
                className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-[#FEF3E7]"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Download size={32} className="text-[#E07A3A]" />
              </motion.div>
            </div>
            <h3 className="mb-1 text-[15px] font-semibold text-[#111827]">
              {t("session.upload.dumping.title")}
            </h3>
            <p className="text-sm text-[#6B7280]">
              {t("session.upload.dumping.body", { count: linesReceived })}
            </p>
          </div>
        )}

        {/* ── PREVIEW step ── */}
        {step === STEPS.PREVIEW && (
          <div>
            {/* Case A: Dump error with no data */}
            {dumpError && (!dumpData || dumpData.files.length === 0) && (
              <div>
                <div className="mb-4 flex items-center gap-2 rounded-lg bg-[#FEF2F2] px-3 py-2.5">
                  <AlertCircle
                    size={14}
                    className="flex-shrink-0 text-[#DC2626]"
                  />
                  <p className="text-xs text-[#DC2626]">{dumpError}</p>
                </div>
                <div className="text-right">
                  <button
                    onClick={handleClose}
                    className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                  >
                    {t("common.actions.close")}
                  </button>
                </div>
              </div>
            )}

            {/* Case B: MAC not matched — allow manual player selection */}
            {!dumpError && dumpData && dumpData.mac && !matchedAssignment && (
              <div>
                <div className="mb-4 flex items-start gap-2 rounded-lg bg-[#FFF7ED] px-3 py-2.5">
                  <AlertTriangle
                    size={14}
                    className="mt-0.5 flex-shrink-0 text-[#E07A3A]"
                  />
                  <p className="text-xs text-[#9A3412]">
                    {t("session.upload.preview.unassigned")}
                  </p>
                </div>
                <div className="mb-4 rounded-lg border border-[#F3F4F6] bg-[#F9FAFB] px-4 py-3">
                  <p className="text-[13px] font-medium text-[#111827]">
                    {wearableLabel}
                  </p>
                  <p className="text-[11px] text-[#9CA3AF]">
                    {t("session.upload.preview.mac", { mac: dumpData.mac })}
                  </p>
                  {dumpData.totalImpacts > 0 && (
                    <p className="mt-1 text-[11px] text-[#6B7280]">
                      {t("session.upload.preview.impactsFound", {
                        count: dumpData.totalImpacts,
                        files: t("session.upload.preview.fileCount", {
                          count: dumpData.files.length,
                        }),
                      })}
                    </p>
                  )}
                </div>

                {/* Upload error banner */}
                {uploadError && (
                  <div className="mb-4 flex items-center gap-2 rounded-lg bg-[#FEF2F2] px-3 py-2.5">
                    <AlertCircle
                      size={14}
                      className="flex-shrink-0 text-[#DC2626]"
                    />
                    <p className="text-xs text-[#DC2626]">{uploadError}</p>
                  </div>
                )}

                {/* Player picker */}
                <PlayerPicker
                  assignments={assignments}
                  selectedId={overrideAssignment?.id || null}
                  onSelect={(a) => setOverrideAssignment(a)}
                />

                {/* Actions */}
                <div className="mt-4 flex items-center justify-end gap-3">
                  <button
                    onClick={handleClose}
                    className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                  >
                    {t("common.actions.cancel")}
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={
                      !overrideAssignment || dumpData.totalImpacts === 0
                    }
                    className="flex items-center gap-2 rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition duration-150 disabled:opacity-40"
                    style={{
                      background:
                        "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                    }}
                  >
                    <Upload size={14} />
                    {t("common.actions.upload")}
                  </button>
                </div>
              </div>
            )}

            {/* Case C: MAC matched */}
            {!dumpError && dumpData && matchedAssignment && (
              <div>
                {/* Upload error banner */}
                {uploadError && (
                  <div className="mb-4 flex items-center gap-2 rounded-lg bg-[#FEF2F2] px-3 py-2.5">
                    <AlertCircle
                      size={14}
                      className="flex-shrink-0 text-[#DC2626]"
                    />
                    <p className="text-xs text-[#DC2626]">{uploadError}</p>
                  </div>
                )}

                {/* Parser warning */}
                {dumpData.error && (
                  <div className="mb-4 flex items-center gap-2 rounded-lg bg-[#FFF7ED] px-3 py-2.5">
                    <AlertTriangle
                      size={14}
                      className="flex-shrink-0 text-[#E07A3A]"
                    />
                    <p className="text-xs text-[#9A3412]">
                      {t("session.upload.preview.partialData", {
                        error: dumpData.error,
                      })}
                    </p>
                  </div>
                )}

                {/* Matched/Override player card */}
                <div
                  className={`mb-4 flex items-center gap-3 rounded-lg border px-4 py-3 ${
                    overrideAssignment
                      ? "border-[#E07A3A]/20 bg-[#FEF7F3]"
                      : "border-[#D1FAE5] bg-[#F0FDF4]"
                  }`}
                >
                  <div
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
                      overrideAssignment ? "bg-[#E07A3A]" : "bg-[#10B981]"
                    }`}
                  >
                    {overrideAssignment ? (
                      <Repeat size={14} className="text-white" />
                    ) : (
                      <Check size={14} className="text-white" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-medium text-[#111827]">
                      {playerName}
                    </p>
                    <p className="text-[11px] text-[#6B7280]">
                      {t("session.upload.preview.sourceImpacts", {
                        source: overrideAssignment
                          ? t("session.upload.preview.manualOverride")
                          : wearableLabel,
                        count: dumpData.totalImpacts,
                      })}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowPlayerPicker((v) => !v)}
                    className="flex items-center gap-1 rounded-lg border border-[#E5E7EB] bg-white px-2.5 py-1.5 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                  >
                    <Repeat size={11} />
                    {t("session.upload.preview.change")}
                  </button>
                </div>

                {/* Player picker (toggled) */}
                {showPlayerPicker && (
                  <div className="mb-4">
                    <PlayerPicker
                      assignments={assignments}
                      selectedId={effectiveAssignment?.id || null}
                      onSelect={(a) => {
                        setOverrideAssignment(
                          a.id === matchedAssignment.id ? null : a
                        );
                        setShowPlayerPicker(false);
                      }}
                    />
                  </div>
                )}

                {/* Impact table */}
                {dumpData.totalImpacts > 0 && (
                  <div className="mb-4 max-h-[280px] overflow-y-auto rounded-lg border border-[#F3F4F6]">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#F3F4F6] bg-[#F9FAFB]">
                          <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">
                            {t("session.upload.preview.columns.file")}
                          </th>
                          <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">
                            {t("session.upload.preview.columns.boot")}
                          </th>
                          <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">
                            {t("session.upload.preview.columns.id")}
                          </th>
                          <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">
                            {t("session.upload.preview.columns.timeMs")}
                          </th>
                          <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">
                            {t("session.upload.preview.columns.linear")}
                          </th>
                          <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">
                            {t("session.upload.preview.columns.rotational")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dumpData.files.flatMap((file, fi) =>
                          file.impacts.map((imp, ii) => (
                            <tr
                              key={`${fi}-${ii}`}
                              className="border-b border-[#F3F4F6] last:border-b-0 hover:bg-[#FAFAFA]"
                            >
                              <td className="px-3 py-2 text-[11px] text-[#6B7280]">
                                {file.filename ||
                                  t("session.upload.preview.fileFallback", {
                                    index: fi + 1,
                                  })}
                              </td>
                              <td className="px-3 py-2 text-[12px] text-[#374151]">
                                {imp.boot}
                              </td>
                              <td className="px-3 py-2 text-[12px] text-[#374151]">
                                {imp.impactId}
                              </td>
                              <td className="px-3 py-2 text-right text-[12px] text-[#374151]">
                                {imp.timestampMs}
                              </td>
                              <td className="px-3 py-2 text-right text-[12px] font-semibold text-[#111827]">
                                {imp.linearG.toFixed(1)}
                              </td>
                              <td className="px-3 py-2 text-right text-[12px] text-[#6B7280]">
                                {imp.rotationalRadS2.toFixed(1)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {dumpData.totalImpacts === 0 && (
                  <div className="mb-4 py-4 text-center">
                    <p className="text-sm text-[#6B7280]">
                      {t("session.upload.preview.noImpacts")}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={handleClose}
                    className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                  >
                    {t("common.actions.cancel")}
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={dumpData.totalImpacts === 0}
                    className="flex items-center gap-2 rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition duration-150 disabled:opacity-40"
                    style={{
                      background:
                        "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                    }}
                  >
                    <Upload size={14} />
                    {t("common.actions.upload")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Serial Terminal (DUMPING + PREVIEW) ── */}
        {(step === STEPS.DUMPING || step === STEPS.PREVIEW) && (
          <SerialTerminal
            receivedData={receivedData}
            connected={connected}
            className="mt-4"
          />
        )}

        {/* ── UPLOADING step ── */}
        {step === STEPS.UPLOADING && (
          <div className="flex flex-col items-center py-6 text-center">
            <motion.div
              className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#FEF3E7]"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Upload size={32} className="text-[#E07A3A]" />
            </motion.div>
            <h3 className="mb-1 text-[15px] font-semibold text-[#111827]">
              {t("session.upload.uploading.title")}
            </h3>
            <p className="text-sm text-[#6B7280]">
              {t("session.upload.uploading.body", {
                count: dumpData?.totalImpacts || 0,
              })}
            </p>
          </div>
        )}

        {/* ── SUCCESS step ── */}
        {step === STEPS.SUCCESS && (
          <div className="flex flex-col items-center py-6 text-center">
            <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#F0FDF4]">
              <CheckCircle size={32} className="text-[#10B981]" />
            </div>
            <h3 className="mb-2 text-[15px] font-semibold text-[#111827]">
              {t("session.upload.success.title")}
            </h3>
            <p className="mb-6 max-w-[320px] text-sm text-[#6B7280]">
              {t("session.upload.success.body", {
                count: uploadedCount,
                name: uploadedPlayerName,
              })}
            </p>
            <button
              onClick={handleDone}
              className="flex items-center gap-2 rounded-[10px] px-6 py-2.5 text-sm font-semibold text-white transition duration-150"
              style={{
                background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
              }}
            >
              <Check size={14} />
              {t("common.actions.done")}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default WearableUploadModal;
